"use client";

import { c as _c } from "react/compiler-runtime";
import { Icon, IconType } from "@/components/formatters/MXPIcon";
import Image from "next/image";
import { TerraLink } from "../../component/TerraLink/TerraLink";
import { ExternalForm, ExternalFormProps } from "@/components/terra/component/ExternalForm/ExternalForm";
import { CommonDescription } from "@/components/terra/component/CommonDescription/CommonDescription";
import darkBgImage from "@/images/dark-texture.webp";
import { IMAGE_QUALITY_DESKTOP } from "@/lib/heroImageUtils";
import { layoutHeaderClasses, layoutHeaderBigClasses, layoutSubheaderClasses, layoutEyebrowClasses } from "@/components/tailwindHelpers";
import { darkTextureWrapper, colorSettingDetection, renderHeaderConditionally } from "../../utils/functions";
import { useRef, useState, useEffect } from "react";
import { useScreenSize } from "../../utils/hooks/useScreenSize";
export interface LayoutForm {
  eyebrow?: string;
  header?: string;
  headerElement?: "h1" | "h2";
  subheader?: string;
  description?: string;
  icon?: IconType;
  buttons?: TerraLink[];
  orientation?: "left" | "right";
  formTheme?: "t2-action-theme-one" | "t2-action-theme-two" | "t2-action-theme-three";
  sideTheme?: "t2-action-theme-one" | "t2-action-theme-two" | "t2-action-theme-three";
  isHeaderBig?: boolean;
  isWrapped?: boolean;
  form?: ExternalFormProps;
}
const SIDE_BASE = ["py-[50px] z-[1] relative", "min-lg:py-[160px] min-lg:px-20 min-lg:basis-0 min-lg:grow-[2] min-lg:flex min-lg:flex-col"].join(" ");
const FORM_SECTION_BASE = ["py-[50px] z-[1] relative", "min-lg:basis-0 min-lg:grow-[4]", "min-lg:py-[100px] min-lg:px-[165px]"].join(" ");
const THEME_BG = "absolute bottom-0 top-0 w-full";
const CTA_OVERRIDES = ["[&_.t2-button--primary]:bg-gray-m9 [&_.t2-button--primary]:text-white", "[&_.t2-button--primary]:border-2 [&_.t2-button--primary]:border-gray-m9", "[&_.t2-button--primary:hover]:bg-gray-m9 [&_.t2-button--primary:hover]:text-white"].join(" ");
export const LayoutForm = t0 => {
  const $ = _c(75);
  const {
    eyebrow,
    header,
    headerElement: t1,
    subheader,
    description,
    icon,
    buttons,
    orientation: t2,
    formTheme: t3,
    sideTheme: t4,
    isHeaderBig: t5,
    isWrapped: t6,
    form
  } = t0;
  const headerElement = t1 === undefined ? "h2" : t1;
  const orientation = t2 === undefined ? "left" : t2;
  const formTheme = t3 === undefined ? "t2-action-theme-three" : t3;
  const sideTheme = t4 === undefined ? "t2-action-theme-one" : t4;
  const isHeaderBig = t5 === undefined ? false : t5;
  const isWrapped = t6 === undefined ? false : t6;
  const formColRef = useRef(null);
  const contentColRef = useRef(null);
  const [formBgLeftPos, setFormBgLeftPos] = useState(0);
  const [formBgRightPos, setFormBgRightPos] = useState(0);
  const [contentBgLeftPos, setContentBgLeftPos] = useState(0);
  const [contentBgRightPos, setContentBgRightPos] = useState(0);
  const [bgTopPos, setBgTopPos] = useState(0);
  const formBackgroundTheme = formTheme;
  const sideBackgroundTheme = sideTheme;
  const wrapperClasses = isWrapped ? "w-full m-auto max-w-[1440px]" : "";
  const t7 = formBackgroundTheme || "";
  let t8;
  if ($[0] !== t7) {
    t8 = colorSettingDetection(t7);
    $[0] = t7;
    $[1] = t8;
  } else {
    t8 = $[1];
  }
  const formColorSetting = t8;
  const t9 = sideBackgroundTheme || "";
  let t10;
  if ($[2] !== t9) {
    t10 = colorSettingDetection(t9);
    $[2] = t9;
    $[3] = t10;
  } else {
    t10 = $[3];
  }
  const sideColorSetting = t10;
  const headerClasses = isHeaderBig ? layoutHeaderBigClasses : layoutHeaderClasses;
  const t11 = header || "";
  let t12;
  if ($[4] !== headerClasses || $[5] !== headerElement || $[6] !== t11) {
    t12 = renderHeaderConditionally(headerClasses, t11, headerElement);
    $[4] = headerClasses;
    $[5] = headerElement;
    $[6] = t11;
    $[7] = t12;
  } else {
    t12 = $[7];
  }
  const finalHeader = t12;
  let t13;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {};
    $[8] = t13;
  } else {
    t13 = $[8];
  }
  const formStyles = t13;
  const t14 = sideBackgroundTheme || "";
  let t15;
  if ($[9] !== t14) {
    t15 = darkTextureWrapper(t14);
    $[9] = t14;
    $[10] = t15;
  } else {
    t15 = $[10];
  }
  const sideStyles = t15;
  const {
    width
  } = useScreenSize();
  const matchingBackgrounds = formBackgroundTheme === sideBackgroundTheme;
  const sidePx = isWrapped ? "px-5 min-lg:px-20" : "";
  const formPx = isWrapped ? "px-5 min-lg:pr-20" : "";
  const sideClasses = `${SIDE_BASE} ${sidePx} ${sideBackgroundTheme || ""} ${sideColorSetting}`;
  const formClasses = `${FORM_SECTION_BASE} ${formPx} ${formBackgroundTheme || ""} ${formColorSetting} ${formTheme === "t2-action-theme-two" ? "bg-transparent!" : ""}`;
  const flexDir = orientation === "right" ? "min-lg:flex-row-reverse" : "min-lg:flex-row";
  let t16;
  let t17;
  if ($[11] !== width) {
    t16 = () => {
      const formColElement = formColRef?.current;
      const contentColElement = contentColRef?.current;
      if (!formColElement || !contentColElement) {
        return;
      }
      const updatePositions = () => {
        requestAnimationFrame(() => {
          const formLeft = formColElement.offsetLeft;
          const contentLeft = contentColElement.offsetLeft;
          const contentWidth = contentColElement.offsetWidth;
          setFormBgLeftPos(formLeft);
          setFormBgRightPos(width - contentLeft);
          setContentBgRightPos(width - (contentLeft + contentWidth));
          setContentBgLeftPos(0);
          if (width > 958) {
            setBgTopPos(formColElement.offsetTop);
          } else {
            setBgTopPos(contentColElement.offsetHeight);
          }
        });
      };
      const observer = new ResizeObserver(updatePositions);
      observer.observe(formColElement);
      observer.observe(contentColElement);
      updatePositions();
      return () => observer.disconnect();
    };
    t17 = [width];
    $[11] = width;
    $[12] = t16;
    $[13] = t17;
  } else {
    t16 = $[12];
    t17 = $[13];
  }
  useEffect(t16, t17);
  let t18;
  if ($[14] !== formBgLeftPos || $[15] !== formBgRightPos || $[16] !== orientation) {
    t18 = orientation === "left" ? {
      left: `${formBgLeftPos}px`
    } : {
      right: `${formBgRightPos}px`
    };
    $[14] = formBgLeftPos;
    $[15] = formBgRightPos;
    $[16] = orientation;
    $[17] = t18;
  } else {
    t18 = $[17];
  }
  const formBgImagePos = t18;
  const t19 = `${bgTopPos}px`;
  let t20;
  if ($[18] !== t19) {
    t20 = {
      top: t19
    };
    $[18] = t19;
    $[19] = t20;
  } else {
    t20 = $[19];
  }
  const bgTopPosition = t20;
  let t21;
  if ($[20] !== bgTopPosition || $[21] !== formBgImagePos) {
    t21 = {
      ...formStyles,
      ...formBgImagePos,
      ...bgTopPosition
    };
    $[20] = bgTopPosition;
    $[21] = formBgImagePos;
    $[22] = t21;
  } else {
    t21 = $[22];
  }
  const formThemeBgStyles = t21;
  let t22;
  if ($[23] !== contentBgLeftPos || $[24] !== contentBgRightPos || $[25] !== orientation) {
    t22 = orientation === "left" ? {
      right: `${contentBgRightPos - 17}px`
    } : {
      left: `${contentBgLeftPos}px`
    };
    $[23] = contentBgLeftPos;
    $[24] = contentBgRightPos;
    $[25] = orientation;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  const contentBgImagePos = t22;
  let t23;
  if ($[27] !== bgTopPosition || $[28] !== contentBgImagePos || $[29] !== sideStyles) {
    t23 = {
      ...sideStyles,
      ...contentBgImagePos,
      ...bgTopPosition
    };
    $[27] = bgTopPosition;
    $[28] = contentBgImagePos;
    $[29] = sideStyles;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  const contentThemeBgStyles = t23;
  const t24 = `t2-layout__content min-lg:flex min-lg:justify-around min-lg:flex-wrap ${flexDir}`;
  let t25;
  if ($[31] !== icon) {
    t25 = icon && <Icon className="bg-yellow-m10 p-[5px] mr-10 text-center mb-[15px]" size={45} type={icon} />;
    $[31] = icon;
    $[32] = t25;
  } else {
    t25 = $[32];
  }
  let t26;
  if ($[33] !== eyebrow) {
    t26 = <div className={layoutEyebrowClasses}>{eyebrow}</div>;
    $[33] = eyebrow;
    $[34] = t26;
  } else {
    t26 = $[34];
  }
  let t27;
  if ($[35] !== subheader) {
    t27 = subheader && <div className={layoutSubheaderClasses}>{subheader}</div>;
    $[35] = subheader;
    $[36] = t27;
  } else {
    t27 = $[36];
  }
  let t28;
  if ($[37] !== description) {
    t28 = <CommonDescription description={description} className="t2-layout-form__description t2-layout__description" />;
    $[37] = description;
    $[38] = t28;
  } else {
    t28 = $[38];
  }
  let t29;
  if ($[39] !== buttons) {
    t29 = buttons && buttons.map(_temp);
    $[39] = buttons;
    $[40] = t29;
  } else {
    t29 = $[40];
  }
  let t30;
  if ($[41] !== t29) {
    t30 = <div className={CTA_OVERRIDES}>{t29}</div>;
    $[41] = t29;
    $[42] = t30;
  } else {
    t30 = $[42];
  }
  let t31;
  if ($[43] !== finalHeader || $[44] !== t25 || $[45] !== t26 || $[46] !== t27 || $[47] !== t28 || $[48] !== t30) {
    t31 = <div className="max-w-[640px] mx-auto z-[1] relative">{t25}{t26}{finalHeader}{t27}{t28}{t30}</div>;
    $[43] = finalHeader;
    $[44] = t25;
    $[45] = t26;
    $[46] = t27;
    $[47] = t28;
    $[48] = t30;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== sideClasses || $[51] !== t31) {
    t32 = <div className={sideClasses} ref={contentColRef}>{t31}</div>;
    $[50] = sideClasses;
    $[51] = t31;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  const t33 = `t2-layout-form__content-theme-background ${THEME_BG} min-lg:!top-0 max-lg:!right-0 max-lg:left-0 bg-cover bg-center bg-no-repeat ${sideTheme} ${matchingBackgrounds ? "!inset-0" : ""}`;
  let t34;
  if ($[53] !== contentThemeBgStyles || $[54] !== t33) {
    t34 = <div className={t33} style={contentThemeBgStyles} />;
    $[53] = contentThemeBgStyles;
    $[54] = t33;
    $[55] = t34;
  } else {
    t34 = $[55];
  }
  let t35;
  if ($[56] !== formTheme || $[57] !== formThemeBgStyles || $[58] !== matchingBackgrounds) {
    t35 = !matchingBackgrounds && <div className={`t2-layout-form__form-theme-background ${THEME_BG} min-lg:!top-0 max-lg:!left-0 max-lg:right-0 bg-cover bg-center bg-no-repeat ${formTheme}`} style={formThemeBgStyles}>{formTheme === "t2-action-theme-two" && <Image src={darkBgImage} alt="" loading="lazy" quality={IMAGE_QUALITY_DESKTOP} className="w-auto h-full object-cover" style={{
        objectPosition: "5% 100%"
      }} />}</div>;
    $[56] = formTheme;
    $[57] = formThemeBgStyles;
    $[58] = matchingBackgrounds;
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  let t36;
  if ($[60] !== form || $[61] !== formBackgroundTheme) {
    t36 = form && <ExternalForm {...form} backgroundTheme={formBackgroundTheme} buttonDisabled={true} />;
    $[60] = form;
    $[61] = formBackgroundTheme;
    $[62] = t36;
  } else {
    t36 = $[62];
  }
  let t37;
  if ($[63] !== formClasses || $[64] !== t36) {
    t37 = <div className={formClasses} ref={formColRef}>{t36}</div>;
    $[63] = formClasses;
    $[64] = t36;
    $[65] = t37;
  } else {
    t37 = $[65];
  }
  let t38;
  if ($[66] !== t24 || $[67] !== t32 || $[68] !== t34 || $[69] !== t35 || $[70] !== t37) {
    t38 = <div className={t24}>{t32}{t34}{t35}{t37}</div>;
    $[66] = t24;
    $[67] = t32;
    $[68] = t34;
    $[69] = t35;
    $[70] = t37;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  let t39;
  if ($[72] !== t38 || $[73] !== wrapperClasses) {
    t39 = <div className="t2-layout-form relative overflow-hidden" data-testid="layout-form"><div className={wrapperClasses}>{t38}</div></div>;
    $[72] = t38;
    $[73] = wrapperClasses;
    $[74] = t39;
  } else {
    t39 = $[74];
  }
  return t39;
};
function _temp(btn, index) {
  return <TerraLink key={index} {...btn} />;
}